import React, { useState, useEffect } from "react";
import { usePermissions } from "../../../PermissionsProvider";
import { IntentService } from "../../../services/IntentService";
import { IntentGroup } from "../../../model_gen/intent";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { SearchIcon, CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import { cn } from "../../../lib/utils";

interface TestSetIntentGroupProps {
  intentGroupId?: number;
  onChange: (intentGroupId: number) => void;
}

const TestSetIntentGroup: React.FC<TestSetIntentGroupProps> = ({
  intentGroupId,
  onChange,
}) => {
  const { apiKey } = usePermissions();
  const [intentGroups, setIntentGroups] = useState<IntentGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    fetchIntentGroups();
  }, [apiKey]);

  const fetchIntentGroups = () => {
    setLoading(true);
    IntentService.getIntentGroups(0, 1000, setLoading, apiKey).subscribe(
      (response: any) => {
        // Use the same pattern as IntentGroupsOverview
        const groups = response.intent_groups || [];
        setIntentGroups(groups);
        setLoading(false);
      },
      (error: any) => {
        console.error("Error fetching intent groups:", error);
        setLoading(false);
      }
    );
  };

  const selectedIntentGroup = intentGroups.find(group => group.id === intentGroupId);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-gc-med text-foreground mb-2">
          Select Intent Group
        </h3>
        <p className="text-sm text-muted-foreground">
          Choose the intent group for which you want to create a test set. Test sets are used to evaluate model performance for specific intents.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="intent-group">Intent Group</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between"
              disabled={loading}
            >
              {selectedIntentGroup
                ? selectedIntentGroup.intent_name
                : loading
                ? "Loading intent groups..."
                : "Select intent group..."}
              <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0" align="start">
            <Command>
              <CommandInput placeholder="Search intent groups..." />
              <CommandList>
                <CommandEmpty>No intent groups found.</CommandEmpty>
                <CommandGroup>
                  {intentGroups.map((group) => (
                    <CommandItem
                      key={group.id}
                      value={group.intent_name}
                      onSelect={() => {
                        onChange(group.id);
                        setOpen(false);
                      }}
                    >
                      <CheckIcon
                        className={cn(
                          "mr-2 h-4 w-4",
                          intentGroupId === group.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col">
                        <span className="font-gc-med">{group.intent_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {group.request_count} requests • Last activity: {group.last_activity ? new Date(group.last_activity).toLocaleDateString() : 'Never'}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {selectedIntentGroup && (
        <Card className="border-maitai-green bg-maitai-green/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 bg-maitai-green text-white rounded-full">
                <CheckIcon className="w-4 h-4" />
              </div>
              <div>
                <div className="text-sm font-gc-med text-foreground">
                  Selected: {selectedIntentGroup.intent_name}
                </div>
                <div className="text-xs text-muted-foreground">
                  {selectedIntentGroup.request_count} requests available
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {intentGroupId && (
        <div className="pt-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            <strong>Next:</strong> You'll be able to provide a name and description for your test set, then select specific requests from this intent group.
          </div>
        </div>
      )}
    </div>
  );
};

export default TestSetIntentGroup; 